// Learn.jsx - The article index. Articles are markdown files in src/content, so
// this list is compiled into the bundle and every page prerenders as real HTML.

import { useState } from "react";
import { Link } from "react-router";
import clsx from "clsx";

import { Seo, JsonLd, breadcrumbSchema, SITE } from "../lib/seo.jsx";
import { ARTICLES, CATEGORIES } from "../lib/content.js";
import { day } from "../lib/format.js";
import { ArrowRight } from "../ui/icons.jsx";
import { Reveal, StaggerBoxed } from "../ui/motion.jsx";
import { Loupe } from "../ui/illustrations.jsx";
import { PageHead, CtaBand, MobileCta } from "./parts.jsx";

export default function Learn() {
  const [category, setCategory] = useState("All");
  const list = category === "All" ? ARTICLES : ARTICLES.filter((article) => article.category === category);
  const [lead, ...rest] = list;

  return (
    <>
      <Seo
        title="Guides to selling precious metals"
        path="/learn"
        description="Plain guides to gold and silver: what scrap is worth, how testing works, how to read hallmarks, and how to avoid the buyers who count on you not knowing."
        image={SITE.ogLearn}
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Learn", to: "/learn" }])} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Blog",
        name: `${SITE.name} guides`,
        url: `${SITE.origin}/learn`,
        blogPost: ARTICLES.slice(0, 20).map((article) => ({
          "@type": "BlogPosting",
          headline: article.title,
          description: article.description,
          datePublished: article.date,
          url: `${SITE.origin}/learn/${article.slug}`,
        })),
      }} />

      <PageHead
        eyebrow="Guides"
        title="What we wish everyone knew before they sold."
        lede="No filler, no affiliate links, and nothing written to make our offers look better than they are. Just the things that cost people money because nobody told them."
        art={() => <Loupe className="max-w-[14rem] ml-auto" />}
      />

      <div className="shell pb-14 md:pb-24">
        {CATEGORIES.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-8 md:mb-10 pb-6 border-b border-rule">
            {["All", ...CATEGORIES].map((name) => (
              <button
                key={name}
                type="button"
                onClick={() => setCategory(name)}
                className={clsx("btn btn-sm", category === name ? "btn-ink" : "btn-quiet")}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {lead && (
          <Reveal as={Link} to={`/learn/${lead.slug}`} className="block group border-b border-rule pb-9 mb-9 md:pb-10 md:mb-10">
            <p className="eyebrow mb-3">{lead.category} &middot; {lead.readMinutes} min read</p>
            <h2 className="display display-lg text-ink max-w-3xl group-hover:text-bronze-dark transition-colors">
              {lead.title}
            </h2>
            <p className="lede mt-4 measure-wide text-muted">{lead.description}</p>
            <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-ink">
              Read it <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </span>
          </Reveal>
        )}

        <StaggerBoxed as="div" step={70} className="grid gap-x-10 gap-y-8 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((article) => (
            <Link key={article.slug} to={`/learn/${article.slug}`} className="group border-t border-rule pt-5 block h-full">
              <p className="eyebrow mb-2.5">{article.category}</p>
              <h3 className="display display-sm text-ink group-hover:text-bronze-dark transition-colors">
                <span className="underline-grow">{article.title}</span>
              </h3>
              <p className="hint mt-2 leading-relaxed line-clamp-3">{article.description}</p>
              <p className="text-[12.5px] text-faint mt-3">
                {article.date ? day(article.date) : ""} &middot; {article.readMinutes} min read
              </p>
            </Link>
          ))}
        </StaggerBoxed>
      </div>

      <MobileCta />

      <CtaBand
        title="Reading is one thing. Weighing is another."
        body="Ask for a free insured kit and find out exactly what is in that drawer, with every weight and test in writing."
      />
    </>
  );
}
